import * as React from 'react';
import { FC, useCallback, useEffect, useMemo } from 'react';

import { CardModal } from 'components/card-modal';
import { useRoute } from 'hooks/navigation/use-route';
import { CustomEventNames, logQuestEvent } from 'state/amplitude';
import noop from 'utils/noop';
import { routes } from 'utils/routing';

import { useCmsQuestDetailLazyQuery } from '../cms-hooks/use-cms-quest-detail';
import { QuestProvider } from '../loyalty-quest';
import { useActivateQuest } from '../loyalty-quest/use-activate-quest';
import { canActivateQuest } from '../loyalty-quest/utils';
import { QuestCardSkeleton } from '../quest-card';
import { QuestDrawerContent } from '../quest-drawer-content';
import { QuestDetailError } from '../quest-drawer-content/quest-detail-error';
import { QuestDrawerSkeleton } from '../quest-drawer-content/quest-detail-skeleton';
import { useParseQuestCardData } from '../use-parse-quest-card-data';
import { useUserQuest } from '../use-user-quest';
import { getProductRequirementCmsIds } from '../utils';

export const QuestDetailsPage: FC<React.PropsWithChildren<{}>> = () => {
  const { isLoading, userQuest, cmsQuestCardUI } = useUserQuest();
  const { params } = useRoute<{ previousPath?: string }>();
  const [
    activateQuest,
    { loading: loadingActivateQuestMutation, error: activateQuestError },
  ] = useActivateQuest({
    questId: userQuest?.loyaltyQuest.id,
  });
  const questRequirementCmsIds = useMemo(
    () => getProductRequirementCmsIds(userQuest?.loyaltyQuest),
    [userQuest?.loyaltyQuest]
  );
  const [
    cmsQuestDetailQuery,
    {
      loadingCmsQuestDetail,
      cmsQuestDetail,
      cmsQuestDetailUI,
      refetch: refetchCmsQuestDetail,
      error: cmsQuestDetailError,
    },
  ] = useCmsQuestDetailLazyQuery({ cmsId: userQuest?.loyaltyQuest.cmsId, questRequirementCmsIds });
  const { title } = useParseQuestCardData(
    userQuest?.loyaltyQuest,
    userQuest?.cmsQuestCard,
    cmsQuestCardUI,
    cmsQuestDetail
  );
  const questDetailError = activateQuestError || cmsQuestDetailError;
  const loadingQuestDetail = loadingCmsQuestDetail || loadingActivateQuestMutation;

  const onRefetch = useCallback(() => {
    if (canActivateQuest(userQuest?.loyaltyQuest.status)) {
      activateQuest();
    }

    refetchCmsQuestDetail();
  }, [activateQuest, refetchCmsQuestDetail, userQuest?.loyaltyQuest.status]);

  useEffect(() => {
    if (!loadingQuestDetail && userQuest?.loyaltyQuest.id && !questDetailError) {
      // If quest exists open the drawer and activate challenge
      logQuestEvent(
        {
          name: CustomEventNames.QUEST_DETAILS_VIEW,
          quest: userQuest?.loyaltyQuest,
          cmsQuestCard: userQuest?.cmsQuestCard,
        },
        { skipLoggingToBraze: true }
      );

      if (canActivateQuest(userQuest?.loyaltyQuest.status)) {
        activateQuest();
      }
      cmsQuestDetailQuery();
    }
  }, [
    activateQuest,
    cmsQuestDetailQuery,
    loadingQuestDetail,
    userQuest?.cmsQuestCard,
    userQuest?.loyaltyQuest,
  ]);

  if (isLoading) {
    return <QuestCardSkeleton />;
  }

  if (!userQuest || !cmsQuestCardUI) {
    return null;
  }

  const dismissType = params.previousPath?.includes(routes.loyaltyActivateQuest) ? 'back' : 'close';

  return (
    <CardModal headerText={title} dismissType={dismissType} standardPadding scrollView>
      {questDetailError ? (
        <QuestDetailError refetch={onRefetch} />
      ) : (
        <QuestProvider quest={userQuest?.loyaltyQuest}>
          {loadingQuestDetail && <QuestDrawerSkeleton />}
          {!loadingQuestDetail && cmsQuestDetail && cmsQuestDetailUI && (
            <QuestDrawerContent
              cmsQuestDetail={cmsQuestDetail}
              cmsQuestDetailUI={cmsQuestDetailUI}
              onCloseDrawer={noop}
            />
          )}
        </QuestProvider>
      )}
    </CardModal>
  );
};
